import type { ReactNode, RefObject } from 'react';
import Header, { HeaderProps } from './Header';
import { cn } from '../../utils/cn';
import { useScrollTopOnMount } from '../../hooks/useScrollTopOnMount';
import { useLocation } from 'react-router-dom';
import MobileHero from './MobileHero';

export const TOPBAR_DEFAULT_CLASS =
    'mb-6 flex pt-[max(0.5rem,env(safe-area-inset-top))] md:mb-0 md:absolute md:inset-x-0 md:top-0 md:z-20 md:bg-white/90 md:backdrop-blur md:pt-[72px] md:pb-3';

export const TOPBAR_LANDING_CLASS =
    'mb-6 hidden pt-[max(0.5rem,env(safe-area-inset-top))] md:mb-0 md:flex md:absolute md:inset-x-0 md:top-0 md:z-20 md:bg-white/90 md:backdrop-blur md:pt-[72px] md:pb-3';

export const CONTENT_TOP_SPACING_CLASS = 'mt-9 md:mt-16 lg:mt-[7.5rem]';

export const FORM_CONTENT_CLASS = 'flex flex-col gap-6 md:gap-10 w-full';

export const PAGE_BODY_TOP_CLASS = 'mt-6 lg:mt-10';

export const CONDITIONS_VIEWPORT_CLASS =
    'relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden';

export const CONDITIONS_SCROLL_HOST_CLASS =
    'relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden';

export const CONDITIONS_SCROLL_CLASS =
    'flex min-h-0 flex-col overflow-y-auto scroll-smooth [-webkit-overflow-scrolling:touch]';

export const CONDITIONS_FADE_CLASS =
    'pointer-events-none absolute bottom-0 left-0 right-0 z-[1] h-20 bg-gradient-to-b from-white/0 to-white';

export const CONDITIONS_SCROLL_DOCK_CLASS =
    'pointer-events-none flex shrink-0 justify-end max-md:absolute max-md:right-0 max-md:bottom-0 max-md:z-[2] max-md:py-3 md:pt-2 [&_button]:pointer-events-auto [&_button]:transition-transform [&_button]:duration-250';

export const CONDITIONS_SCROLL_DOCK_AT_BOTTOM_CLASS = 'max-md:bottom-[5.5rem]';

export const CONDITIONS_ACTIONS_CLASS =
    'flex flex-col gap-4 pt-6 pb-[max(1rem,env(safe-area-inset-bottom))]';

export const STICKY_FOOTER_CLASS = 'sticky bottom-0';

const FOOTER_CLASS = 'mt-auto flex shrink-0 flex-col md:mt-0';

const LAYOUT_CONTENT_CLASS = {
    standard: 'flex flex-col',
    landing: 'flex flex-col',
    conditions: '',
} as const;

export type RegistrationPageProps = {
    headerProps?: HeaderProps;
    layout?: 'standard' | 'landing' | 'conditions';
    topBarClassName?: string;
    columnClassName?: string;
    contentTopClassName?: string;
    contentWrapperClassName?: string;
    contentClassName?: string;
    contentRef?: RefObject<HTMLDivElement>;
    footer?: ReactNode;
    footerClassName?: string;
    stickyFooter?: boolean;
    accessory?: ReactNode;
    conditionsDock?: ReactNode;
    children: ReactNode;
};

export default function RegistrationPage({
    layout = 'standard',
    topBarClassName,
    columnClassName,
    contentTopClassName,
    contentWrapperClassName,
    contentClassName,
    contentRef,
    footer,
    footerClassName,
    accessory,
    conditionsDock,
    children,
    headerProps,
    stickyFooter,
}: RegistrationPageProps) {
    useScrollTopOnMount();

    const { pathname } = useLocation();
    const showHeroOnMobile = pathname === '/';

    const topSpacing = contentTopClassName ?? CONTENT_TOP_SPACING_CLASS;
    const landingFooterClass =
        layout === 'landing'
            ? 'mt-auto w-full max-w-[26rem] min-[450px]:max-w-none flex shrink-0 flex-col'
            : FOOTER_CLASS;

    const header = (
        <Header {...headerProps} className={cn(topBarClassName, headerProps?.className)} />
    );

    const footerNode = footer ? (
        <div
            className={cn(landingFooterClass, stickyFooter && STICKY_FOOTER_CLASS, footerClassName)}
        >
            {footer}
        </div>
    ) : null;

    if (layout === 'conditions') {
        return (
            <div className="flex min-h-0 w-full flex-1 flex-col">
                {header}
                <div className={cn(CONDITIONS_VIEWPORT_CLASS, columnClassName)}>
                    <div className={CONDITIONS_SCROLL_HOST_CLASS}>
                        <div
                            ref={contentRef}
                            className={cn(CONDITIONS_SCROLL_CLASS, topSpacing, contentClassName)}
                        >
                            {children}
                        </div>
                        {accessory}
                    </div>
                    {conditionsDock}
                </div>
                {footerNode}
            </div>
        );
    }

    return (
        <div className={cn('flex min-h-0 w-full flex-1 flex-col')}>
            {header}
            {showHeroOnMobile && <MobileHero />}
            <div className="flex flex-col min-h-0 h-full md:h-auto overflow-y-auto">
                <div className={cn('flex flex-col', contentWrapperClassName)}>
                    <div
                        className={cn(
                            LAYOUT_CONTENT_CLASS[layout],
                            topSpacing,
                            contentClassName,
                            'p-1'
                        )}
                    >
                        {children}
                    </div>
                </div>
                {footerNode}
            </div>
        </div>
    );
}

export function RegistrationSubmitFooter({
    children,
    className,
}: {
    children: ReactNode;
    className?: string;
}) {
    return (
        <div
            className={cn(
                'mx-auto flex w-full max-w-md min-[450px]:max-w-none flex-col items-stretch md:mt-8 md:items-center',
                className
            )}
        >
            {children}
        </div>
    );
}
